import React, { useState } from 'react'
import useSWRMutation from 'swr/mutation'
import { useNavigate } from 'react-router-dom'

import FormGroup from '../../components/form/FormGroup'
import TextInput from '../../components/form/TextInput'
import { patchOnboardingUser } from '../../../api/users'
import ErrorMessage from '../../components/errors/ErrorMessage'
import { useI18N } from '../../../libraries/i18n/hook'
import useForm from '../../../libraries/form/hook'
import { type User } from '../../../api/users/types'
import { CurrentUserContext } from '../../../libraries/auth'
import FloatingSubmitButton from './FloatingSubmitButton'
import { showLinkedinVerificationBeforeOnBoarding } from '../../../libraries/auth/linkedinVerification'

type Props = { onSuccess?: () => void }
const PersonalDetailsForm: React.FC<Props> = ({ onSuccess }) => {
  const { t } = useI18N()
  const navigate = useNavigate()
  const { currentUser, linkedinProfile } = React.useContext(CurrentUserContext)
  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false)

  if(showLinkedinVerificationBeforeOnBoarding(!!currentUser?.onboardingCompleted) && !currentUser?.linkedinSub && !linkedinProfile?.sub) {
    navigate('/onboarding/linkedin')
  }

  const { fields, error, submitting, handleChange, handleSubmit } = useForm<Partial<User>, User | undefined, '/users'>({
    initialValues: {
      name: currentUser?.name ?? linkedinProfile?.givenName ?? '',
      lastName: currentUser?.lastName ?? linkedinProfile?.familyName ?? '',
      onboardingCompleted: currentUser?.onboardingCompleted ?? false
    },
    mutation: useSWRMutation('/users', patchOnboardingUser),
    onSuccess,
  })

  const ctaText = fields.onboardingCompleted ? t('common.continue') : t('common.letsStart')

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!fields.onboardingCompleted && !acceptPrivacyPolicy) {
      // Show an error or prevent form submission
      return
    }
    handleSubmit(e)
  }

  const privacyPolicyUrl = 'https://zrive-platform-content.s3.eu-west-1.amazonaws.com/public/Política+de+Privacidad+Bloom.pdf'

  return (
    <form onSubmit={handleFormSubmit} className='w-full'>
      <FormGroup><TextInput value={fields.name} className='w-full' label={t('common.name')} placeholder={t('common.name')} name='name' onChange={handleChange} required /></FormGroup>
      <FormGroup><TextInput value={fields.lastName} className='w-full' label={t('common.lastName')} placeholder={t('common.lastName')} name='lastName' onChange={handleChange} required /></FormGroup>
      {!fields.onboardingCompleted && (
        <FormGroup>
          <label className='flex items-center'>
            <input
              type='checkbox'
              checked={acceptPrivacyPolicy}
              onChange={(e) => setAcceptPrivacyPolicy(e.target.checked)}
              required
              className='mr-2 ml-2'
            />
            <span className='text-sm'>{t('common.acceptPrivacyPolicy')} <a href={privacyPolicyUrl} target='_blank' rel='noopener noreferrer' className='text-blue-600 hover:underline'>{t('common.privacyPolicy')}</a>.</span>
          </label>
        </FormGroup>
      )}
      <FloatingSubmitButton disabled={submitting}>
        {ctaText}
      </FloatingSubmitButton>
      {error && <ErrorMessage error={error} />}
    </form>
  )
}

export default PersonalDetailsForm
